import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { checkAuthStatus, logoutUser } from './api/BackendApi'; 
import AnalyticsTracker from './cookies/AnalyticsTracker'; 

import Header from './components/Header'; 
import Footer from './components/Footer'; 
import HomePage from './pages/HomePage';
import CookieBanner from './cookies/CookieBanner';
import CookiePolicyPage from './pages/CookiePolicyPage';
import TermsPage from './pages/TermsPage';

import CreateAnalysisPage from './pages/CreateAnalysis/CreateAnalysisPage'; 
import FillInvoiceDataPage from './pages/CreateAnalysis/FillInvoiceDataPage.jsx';
import EnterEmailStep from './pages/CreateAnalysis/EnterEmailStep';
import AnalysisSummaryPage from './pages/CreateAnalysis/AnalysisSummaryPage.jsx';
import AnalysisPagesPage from './pages/CreateAnalysis/AnalysisPagesPage.jsx';
import DashboardPage from './pages/Dashboard'; 

import CalculatorPage from './pages/payments/CalculatorPage.jsx';
import PaymentHistoryPage from './pages/payments/PaymentHistoryPage.jsx';
import BuyCreditPage from './pages/payments/BuyCreditPage.jsx';
import PaymentSuccessPage from './pages/payments/PaymentSuccessPage.jsx';
import PaymentFailedPage from './pages/payments/PaymentFailedPage.jsx';

import LoginPage from './pages/user/LoginPage';
import RegisterPage from './pages/user/RegisterPage';
import ForgotPasswordPage from './pages/user/ForgotPasswordPage';
import ResetPasswordPage from './pages/user/ResetPasswordPage'; 
import ConfirmEmailPage from './pages/user/ConfirmEmailPage'; 
import AccountPage from './pages/user/AccountPage';
import EditAccountPage from './pages/user/EditAccountPage';
import ChangePasswordPage from './pages/user/ChangePasswordPage';

const Layout = ({ isAuthenticated, isTemporary, user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Simple logic to highlight the active tab in your Header
  // (You should eventually refactor Header to use <Link> or <NavLink>)
  let activePage = 'home';
  if (location.pathname.includes('/dashboard')) activePage = 'dashboard';
  else if (location.pathname.includes('/analysis/create')) activePage = 'create';
  else if (location.pathname.includes('/login')) activePage = 'login';
  
  // Simple adapter for Header navigation
  const handleHeaderNav = (page) => {
    if (page === 'home') navigate('/');
    else if (page === 'dashboard') navigate('/dashboard');
    else if (page === 'create') navigate('/analysis/create');
    else if (page === 'logout') onLogout(); // Handle logout route or logic
    else navigate(`/${page}`);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 font-sans text-gray-900">
       <Header
          currentPage={activePage}
          setCurrentPage={handleHeaderNav} 
          isAuthenticated={isAuthenticated}
          userInfo={user}
        />
        <main className="flex-grow pt-24">
          {/* Outlet renders the child route matching the URL */}
          <Outlet /> 
        </main>

        <Footer />
    </div>
  );
};

const RequireAuth = ({ isAuthenticated, children }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};
const RequireFullUser = ({ isAuthenticated, isTemporary, children }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isTemporary) {
    return <Navigate to="/analysis/create" replace />;
  }

  // Full user -> Access granted
  return children;
};

// 3. MAIN APP
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [user, setUser] = useState({
    username: "neznámý uživatel",
    balance: 0,
    isTemporary: false
  });

  const refreshAuth = useCallback(async () => {
    setIsAuthLoading(true);
    try {
      const data = await checkAuthStatus();
      if (data.isAuthenticated) {
        setIsAuthenticated(true);
        setUser({
          username: data.username, 
          balance: data.balance, 
          isTemporary: data.is_temporary || false
        });
      } else {
        setIsAuthenticated(false);
        setUser(prev => ({ ...prev, isTemporary: false }));
      }
      return data;
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      return null;
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  const updateBalance = useCallback((newBalance) => {
    setUser(prev => ({ ...prev, balance: newBalance }));
  }, []);

  const handleLogout = useCallback(async (redirectTo = '/') => {
    try {
        await logoutUser(); // Call your backend to clear cookies
    } catch (e) {
        console.error("Logout failed", e);
    } finally {
        // Clear state regardless of whether the backend call succeeded or failed
        setIsAuthenticated(false);
        setUser({
            username: "Neznámý uživatel",
            balance: 0,
            isTemporary: false
        });
        
        window.location.href = typeof redirectTo === 'string' ? redirectTo : '/'; 
    }
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn("Session expired. Logging out and redirecting...");
      handleLogout('/login'); 
    };

    // Listen for the custom event we dispatched from the API file
    window.addEventListener('session-expired', handleSessionExpired);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [handleLogout]);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Loader className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <CookieBanner />

      <Routes>
        <Route element={<Layout isAuthenticated={isAuthenticated} isTemporary={user.isTemporary} user={user} onLogout={handleLogout} />}>
          
          {/* --- Public Routes --- */}
          <Route path="/" element={<HomePage isAuthenticated={isAuthenticated} isTemporary={user.isTemporary} />} />
          <Route path="/zasady-cookies" element={<CookiePolicyPage />} /> 
          <Route path="/terms" element={<TermsPage />} /> 
          <Route path="/login" element={<LoginPage onLoginSuccess={refreshAuth} />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/payments/calculator" element={<CalculatorPage isAuthenticated={isAuthenticated}/>} />

          <Route path="/analysis/email" element={<EnterEmailStep onEmailSuccess={refreshAuth} />} />

          {/* --- Level 2: Basic Auth (Temp + Full Users) --- */}
          
          {/* Analysis Wizard */}
          {/* URL: /analysis/create?step=2&fileId=123 */}
          <Route path="/analysis/create" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <CreateAnalysisPage updateBalance={updateBalance}/>
            </RequireAuth>
          } />

          {/* Analysis Results */}
          {/* URL: /analysis/123/summary */}
          <Route path="/analysis/:id/summary" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <AnalysisSummaryPage /> 
            </RequireAuth>
          } />
          
          {/* URL: /analysis/123/details */}
          <Route path="/analysis/:analysisId/details" element={
            <RequireAuth isAuthenticated={isAuthenticated}>
              <AnalysisPagesPage />
            </RequireAuth>
          } />

          {/* --- Level 3: Strict Auth (Only Full Users) --- */}
          {/* Dashboard */}
          <Route path="/dashboard" element={
            <RequireFullUser isAuthenticated={isAuthenticated} isTemporary={user.isTemporary}>
              <DashboardPage updateBalance={updateBalance}/>
            </RequireFullUser>
          } />

          <Route path="/account" element={
            <RequireFullUser isAuthenticated={isAuthenticated} isTemporary={user.isTemporary}>
              <AccountPage />
            </RequireFullUser>
          } />
          
          <Route path="/account/edit" element={
            <RequireFullUser isAuthenticated={isAuthenticated} isTemporary={user.isTemporary}>
              <EditAccountPage />
            </RequireFullUser>
          } />
          
          <Route path="/account/change-password" element={
            <RequireFullUser isAuthenticated={isAuthenticated} isTemporary={user.isTemporary}>
              <ChangePasswordPage />
            </RequireFullUser>
          } />

          <Route path="/payments/history" element={
            <RequireFullUser isAuthenticated={isAuthenticated} isTemporary={user.isTemporary}>
              <PaymentHistoryPage />
            </RequireFullUser>
          } />
          <Route path="/payments/buy" element={
             <RequireFullUser isAuthenticated={isAuthenticated} isTemporary={user.isTemporary}>
               <BuyCreditPage />
             </RequireFullUser>
          } />
          
          {/* Payment Callbacks (Backend redirects here) */}
          <Route path="/payment/success" element={<PaymentSuccessPage isTemporary={user.isTemporary}/>} />
          <Route path="/payment/failed" element={<PaymentFailedPage />} />

          <Route path="/fill-invoice-data" element={<FillInvoiceDataPage />} />

          {/* Fallback for unknown URLs */}
          <Route path="*" element={<Navigate to="/" replace />} />
          
        </Route>
      </Routes>
    </BrowserRouter>
  );
}